import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/ui-bits";
import { Plus, MapPin, Building2, Search, Settings } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const FALLBACK_BG = "linear-gradient(135deg, #0B1220 0%, #1D3557 100%)";

export default function Embarcaciones() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [e, c] = await Promise.all([
          api.get("/embarcaciones"),
          api.get("/clientes"),
        ]);
        setItems(e.data);
        setClientes(c.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const cliMap = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id, c])), [clientes]);
  const filtered = useMemo(() => {
    if (!q) return items;
    const s = q.toLowerCase();
    return items.filter((x) =>
      x.name?.toLowerCase().includes(s) ||
      x.matricula?.toLowerCase().includes(s) ||
      x.puerto?.toLowerCase().includes(s)
    );
  }, [items, q]);

  const isAdmin = user?.role === "admin";

  return (
    <div className="px-5 pt-8 sns-fade-up" data-testid="page-embarcaciones">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="sns-title text-3xl">{t("emb.title")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("emb.count", { n: items.length })}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => nav("/gestion/embarcaciones")}
              className="w-12 h-12 rounded-full bg-white border border-[var(--border)] flex items-center justify-center text-slate-600 hover:bg-slate-50"
              aria-label={t("emb.manage")}
              data-testid="btn-gestionar-embarcaciones"
            >
              <Settings size={20} />
            </button>
            <button
              onClick={() => nav("/gestion/embarcaciones")}
              className="sns-fab w-12 h-12 flex items-center justify-center"
              data-testid="btn-new-embarcacion"
              aria-label={t("common.new")}
            >
              <Plus size={22} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t("emb.search.placeholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="sns-input pl-11"
          data-testid="search-embarcaciones"
        />
      </div>

      {loading ? <Spinner /> : filtered.length === 0 ? (
        <EmptyState title={t("emb.empty.title")} subtitle={t("emb.empty.sub")} />
      ) : (
        <div className="space-y-4">
          {filtered.map((e) => <EmbCard key={e.id} e={e} cli={cliMap[e.cliente_id]} />)}
        </div>
      )}
    </div>
  );
}

const EmbCard = ({ e, cli }) => (
  <Link to={`/embarcaciones/${e.id}`} className="sns-card overflow-hidden block hover:shadow-md transition-shadow" data-testid={`emb-${e.id}`}>
    <div className="h-44 relative overflow-hidden" style={{ background: FALLBACK_BG }}>
      {e.photo_url && (
        <img src={e.photo_url} alt={e.name}
             className="w-full h-full object-cover"
             onError={(ev) => { ev.target.style.display = "none"; }} />
      )}
      {e.puerto && (
        <div className="absolute top-3 left-3 bg-[var(--ink)]/90 text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-semibold backdrop-blur-sm">
          <MapPin size={12} /> {e.puerto}
        </div>
      )}
    </div>
    <div className="p-5">
      <h3 className="sns-title text-xl mb-1">{e.name}</h3>
      <p className="text-slate-500 text-sm mb-3">{e.description || "—"}</p>
      {e.matricula && (
        <span className="sns-pill bg-[var(--sky)] text-[var(--sky-text)] mb-3">
          {e.matricula}
        </span>
      )}
      {cli && (
        <div className="flex items-center gap-2 pt-3 mt-3 border-t border-[var(--border)] text-slate-600 text-sm">
          <Building2 size={14} className="text-slate-400" />
          <span className="font-semibold">{cli.name}</span>
        </div>
      )}
    </div>
  </Link>
);
